import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ChevronDown, Shield, Star, ArrowRight, Zap, BookOpen, Trophy, Target } from 'lucide-react';
// Images
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/1T6HckQ1yHrof2wJLDoF7d/Diseno_sin_titulo.png';
const BONO_1_IMAGE = 'https://cdn.magicpatterns.com/uploads/vz5fcpC4prWf4pw7VSyYXe/Diseno_sin_titulo_(2).png';
const BONO_2_IMAGE = 'https://cdn.magicpatterns.com/uploads/6WDQ82c8Z4B1xQbcESCR1f/Diseno_sin_titulo_(1).png';
// Purchase Link
const PURCHASE_LINK = 'https://pagos.digitalbibliotec.online/cart/47779890233598:1';
const scrollToPurchase = (e?: React.MouseEvent) => {
  if (e) e.preventDefault();
  const element = document.getElementById('compra');
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
};
const CTAButton = ({
  text,
  subtext,
  className = '',
  isExternal = false
}: {
  text: string;
  subtext?: string;
  className?: string;
  isExternal?: boolean;
}) => <motion.button whileHover={{
  scale: 1.05
}} whileTap={{
  scale: 0.95
}} animate={{
  boxShadow: ['0px 0px 0px rgba(22, 163, 74, 0.7)', '0px 0px 20px rgba(22, 163, 74, 0)', '0px 0px 0px rgba(22, 163, 74, 0)']
}} transition={{
  duration: 2,
  repeat: Infinity
}} onClick={e => {
  if (isExternal) {
    window.location.href = PURCHASE_LINK;
  } else {
    scrollToPurchase(e);
  }
}} className={`w-full md:w-auto bg-green-600 hover:bg-green-700 text-white font-black text-xl md:text-3xl py-4 px-8 rounded-lg shadow-xl border-b-4 border-green-800 uppercase tracking-wide transform transition-all cursor-pointer ${className}`} data-id="element-9">
    <div className="flex flex-col items-center justify-center text-center w-full" data-id="element-10">
      <span className="flex items-center justify-center gap-2 text-center" data-id="element-11">
        {text} <ArrowRight className="w-6 h-6 md:w-8 md:h-8" data-id="element-12" />
      </span>
      {subtext && <span className="text-sm md:text-base font-medium normal-case opacity-90 mt-1 text-center" data-id="element-13">
          {subtext}
        </span>}
    </div>
  </motion.button>;
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-12 md:py-20 px-4 md:px-8 ${className}`} data-id="element-14">
    <div className="max-w-5xl mx-auto" data-id="element-15">{children}</div>
  </section>;
const CheckListItem = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-3 mb-3" data-id="element-16">
    <div className="bg-green-100 rounded-full p-1 mt-1 flex-shrink-0" data-id="element-17">
      <Check className="w-5 h-5 text-green-600" data-id="element-18" />
    </div>
    <span className="text-lg md:text-xl text-gray-800 font-medium" data-id="element-19">{text}</span>
  </li>;
const XListItem = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-3 mb-3" data-id="element-20">
    <div className="bg-red-100 rounded-full p-1 mt-1 flex-shrink-0" data-id="element-21">
      <X className="w-5 h-5 text-red-600" data-id="element-22" />
    </div>
    <span className="text-lg md:text-xl text-gray-700" data-id="element-23">{text}</span>
  </li>;
export function SalesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <div className="font-sans text-gray-900 bg-white overflow-x-hidden" data-id="element-24">
      {/* HERO SECTION */}
      <Section className="bg-white text-center pt-4 pb-8" data-id="element-25">
        <p className="text-xs md:text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wider" data-id="element-26">
          💪 Para personas que quieren entrenar bien sin improvisar ni
          lesionarse
        </p>

        <h1 className="text-3xl md:text-5xl font-black text-black leading-tight mb-1 tracking-tighter" data-id="element-27">
          FITNESS MASTER PACK
        </h1>

        <h2 className="text-xl md:text-3xl font-bold text-blue-900 mb-3" data-id="element-28">
          Entrenamiento + Nutrición
        </h2>

        <div className="my-2 flex justify-center cursor-pointer" onClick={scrollToPurchase} data-id="element-29">
          <img src={PRODUCT_IMAGE} alt="Fitness Master Pack" className="w-full max-w-md hover:scale-105 transition-transform duration-300 drop-shadow-2xl" data-id="element-30" />
        </div>

        <h3 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4 max-w-4xl mx-auto leading-snug" data-id="element-31">
          El sistema completo para aprender a entrenar desde cero hasta nivel
          avanzado
        </h3>

        <p className="text-xl md:text-2xl text-gray-600 font-medium mb-6 max-w-3xl mx-auto" data-id="element-32">
          Sin improvisar. Sin lesiones. Con resultados reales y sostenibles.
        </p>

        <CTAButton text="👉 SÍ, QUIERO ACCEDER AHORA" data-id="element-33" />

        <div className="mt-4 flex items-center justify-center gap-2 text-gray-600 font-semibold" data-id="element-34">
          <Shield className="w-5 h-5 text-green-500" data-id="element-35" />
          <span data-id="element-36">✅ Garantía de satisfacción de 7 días</span>
        </div>
      </Section>

      {/* PAIN SECTION */}
      <Section className="bg-gray-50 border-t border-gray-200" data-id="element-37">
        <div className="max-w-3xl mx-auto" data-id="element-38">
          <h3 className="text-3xl md:text-4xl font-black text-center mb-8 leading-tight" data-id="element-39">
            Si entrenás (o querés entrenar), déjame hacerte una pregunta honesta
          </h3>

          <p className="text-xl text-gray-700 mb-8 font-medium" data-id="element-40">
            ¿Te pasa que entrenás… pero sentís que no estás progresando como
            deberías?
          </p>

          <p className="text-lg text-gray-600 mb-8" data-id="element-41">
            No porque no tengas ganas. No porque no te esfuerces. No porque "no
            sirvas para entrenar".
            <br data-id="element-42" />
            <br data-id="element-43" />
            Sino porque{' '}
            <span className="font-bold bg-yellow-200 px-1" data-id="element-44">
              nadie te enseñó a entrenar con un sistema claro.
            </span>
          </p>

          <p className="text-xl font-bold mb-6" data-id="element-45">Entrenás, pero:</p>

          <ul className="space-y-2 mb-10" data-id="element-46">
            <XListItem text="No sabés cuántas repeticiones hacer" data-id="element-47" />
            <XListItem text="No sabés cuánto descansar" data-id="element-48" />
            <XListItem text="Cambiás de rutina todo el tiempo" data-id="element-49" />
            <XListItem text="Tenés miedo de lesionarte" data-id="element-50" />
            <XListItem text={'Comés "más o menos bien", pero sin entender por qué'} data-id="element-51" />
            <XListItem text="Seguís consejos contradictorios en redes sociales" data-id="element-52" />
          </ul>

          <div className="bg-white p-6 border-l-4 border-red-500 shadow-md italic text-xl text-gray-700" data-id="element-53">
            Y al final pensás:
            <br data-id="element-54" />
            <span className="font-bold text-2xl text-black" data-id="element-55">
              "Entreno… pero algo estoy haciendo mal."
            </span>
          </div>
        </div>
      </Section>

      {/* PROBLEM AGITATION */}
      <Section className="bg-white" data-id="element-56">
        <div className="max-w-3xl mx-auto text-center" data-id="element-57">
          <h3 className="text-4xl font-black mb-6" data-id="element-58">
            Entrenar no debería sentirse así
          </h3>

          <p className="text-xl text-gray-700 mb-8" data-id="element-59">
            El problema que nadie dice en voz alta es este:
          </p>

          <div className="bg-yellow-100 p-6 rounded-lg border-2 border-yellow-400 mb-10 inline-block" data-id="element-60">
            <p className="text-2xl font-bold text-gray-900" data-id="element-61">
              👉 El problema no sos vos. El problema es entrenar sin un sistema.
            </p>
          </div>

          <p className="text-lg text-left mb-6 font-semibold" data-id="element-62">
            Si entrenás (o intentaste entrenar), esto seguramente te resulta
            familiar:
          </p>

          <div className="grid md:grid-cols-2 gap-4 text-left" data-id="element-63">
            <div className="bg-gray-50 p-4 rounded border border-gray-200" data-id="element-64">
              ❌ Rutinas copiadas sin contexto
            </div>
            <div className="bg-gray-50 p-4 rounded border border-gray-200" data-id="element-65">
              ❌ Información suelta y contradictoria
            </div>
            <div className="bg-gray-50 p-4 rounded border border-gray-200" data-id="element-66">
              ❌ Progreso lento o estancado
            </div>
            <div className="bg-gray-50 p-4 rounded border border-gray-200" data-id="element-67">
              ❌ Dudas constantes
            </div>
            <div className="bg-gray-50 p-4 rounded border border-gray-200" data-id="element-68">
              ❌ Riesgo de lesiones
            </div>
            <div className="bg-gray-50 p-4 rounded border border-gray-200" data-id="element-69">
              ❌ Falta de estructura real
            </div>
          </div>

          <p className="mt-8 text-xl font-medium text-red-600" data-id="element-70">
            Lo peor no es solo el físico. Es la sensación constante de no saber
            si estás haciendo lo correcto.
          </p>
        </div>
      </Section>

      {/* SOLUTION */}
      <Section className="bg-black text-white" data-id="element-71">
        <div className="text-center" data-id="element-72">
          <div className="inline-block bg-green-500 text-white font-bold px-4 py-1 rounded-full mb-6" data-id="element-73">
            ✅ LA SOLUCIÓN
          </div>

          <h2 className="text-5xl md:text-7xl font-black text-yellow-400 mb-6 tracking-tight" data-id="element-74">
            FITNESS MASTER PACK
          </h2>

          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-75">
            Un sistema completo de entrenamiento de fuerza + nutrición, claro y
            progresivo, diseñado para que sepas qué hacer, cuándo hacerlo y por
            qué, sin improvisar y sin poner tu cuerpo en riesgo.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12" data-id="element-76">
            <div className="bg-gray-900 p-6 rounded-xl border border-gray-700" data-id="element-77">
              <div className="text-4xl mb-4" data-id="element-78">📂</div>
              <h4 className="text-xl font-bold mb-2" data-id="element-79">Abrís el material</h4>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-gray-700" data-id="element-80">
              <div className="text-4xl mb-4" data-id="element-81">👆</div>
              <h4 className="text-xl font-bold mb-2" data-id="element-82">Elegís tu nivel</h4>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-gray-700" data-id="element-83">
              <div className="text-4xl mb-4" data-id="element-84">✏️</div>
              <h4 className="text-xl font-bold mb-2" data-id="element-85">
                Aplicás el plan correcto
              </h4>
            </div>
          </div>

          <p className="text-2xl font-bold text-white mb-10" data-id="element-86">
            Sin confusión. Sin rutinas al azar. Sin entrenar a ciegas.
          </p>

          <CTAButton text="👉 QUIERO ENTRENAR CON UN SISTEMA" className="bg-green-500 hover:bg-green-600 text-white border-green-700" data-id="element-87" />
        </div>
      </Section>

      {/* WHAT'S INCLUDED - TOMOS */}
      <Section className="bg-gray-50" data-id="element-88">
        <h2 className="text-3xl md:text-5xl font-black text-center mb-12 text-blue-900" data-id="element-89">
          📘 ¿Qué incluye exactamente el FITNESS MASTER PACK?
        </h2>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-90">
          {/* TOMO 1 */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200" data-id="element-91">
            <div className="bg-blue-900 text-white p-4" data-id="element-92">
              <h3 className="text-xl font-bold flex items-center gap-2" data-id="element-93">
                <span className="text-2xl" data-id="element-94">🧠</span> TOMO 1 – Introducción
              </h3>
            </div>
            <div className="p-6" data-id="element-95">
              <p className="text-gray-500 font-semibold mb-4 uppercase text-sm tracking-wide" data-id="element-96">
                (La base que casi nadie explica bien)
              </p>
              <ul className="space-y-3" data-id="element-97">
                <CheckListItem text="Beneficios reales del entrenamiento de fuerza" data-id="element-98" />
                <CheckListItem text="Niveles: principiante, intermedio y avanzado" data-id="element-99" />
                <CheckListItem text="Entrenamiento según objetivo" data-id="element-100" />
                <CheckListItem text="Repeticiones, descansos e hipertrofia" data-id="element-101" />
                <CheckListItem text="Entrenar al fallo: cuándo sí y cuándo no" data-id="element-102" />
                <CheckListItem text="Técnica, calentamiento y prevención de lesiones" data-id="element-103" />
                <CheckListItem text="Sueño, recuperación y constancia" data-id="element-104" />
              </ul>
              <div className="mt-6 p-3 bg-blue-50 text-blue-800 font-bold text-center rounded-lg" data-id="element-105">
                👉 Ideal tanto para principiantes como para profesionales.
              </div>
            </div>
          </div>

          {/* TOMO 2 */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200" data-id="element-106">
            <div className="bg-blue-800 text-white p-4" data-id="element-107">
              <h3 className="text-xl font-bold flex items-center gap-2" data-id="element-108">
                <span className="text-2xl" data-id="element-109">💪</span> TOMO 2 – Nivel Principiante
              </h3>
            </div>
            <div className="p-6" data-id="element-110">
              <p className="text-gray-500 font-semibold mb-4 uppercase text-sm tracking-wide" data-id="element-111">
                Diseñado para crear una base sólida y segura:
              </p>
              <ul className="space-y-3" data-id="element-112">
                <CheckListItem text="Frecuencia semanal correcta" data-id="element-113" />
                <CheckListItem text="Rutinas de cuerpo completo" data-id="element-114" />
                <CheckListItem text="8 fichas de entrenamiento listas para usar" data-id="element-115" />
                <CheckListItem text="Repeticiones, descansos y cardio" data-id="element-116" />
                <CheckListItem text="Técnica correcta desde el inicio" data-id="element-117" />
              </ul>
              <div className="mt-6 p-3 bg-blue-50 text-blue-800 font-bold text-center rounded-lg" data-id="element-118">
                👉 Empezá sin miedo a lesionarte.
              </div>
            </div>
          </div>

          {/* TOMO 3 */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200" data-id="element-119">
            <div className="bg-blue-700 text-white p-4" data-id="element-120">
              <h3 className="text-xl font-bold flex items-center gap-2" data-id="element-121">
                <span className="text-2xl" data-id="element-122">⚙️</span> TOMO 3 – Nivel Intermedio
              </h3>
            </div>
            <div className="p-6" data-id="element-123">
              <p className="text-gray-500 font-semibold mb-4 uppercase text-sm tracking-wide" data-id="element-124">
                Cuando el cuerpo ya se adaptó, necesitás estructura:
              </p>
              <ul className="space-y-3" data-id="element-125">
                <CheckListItem text="Rutinas de cuerpo completo" data-id="element-126" />
                <CheckListItem text="División AB" data-id="element-127" />
                <CheckListItem text="División ABC" data-id="element-128" />
                <CheckListItem text="Mayor volumen y control" data-id="element-129" />
                <CheckListItem text="Progresión real del entrenamiento" data-id="element-130" />
              </ul>
              <div className="mt-6 p-3 bg-blue-50 text-blue-800 font-bold text-center rounded-lg" data-id="element-131">
                👉 Acá empiezan los cambios visibles.
              </div>
            </div>
          </div>

          {/* TOMO 4 */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200" data-id="element-132">
            <div className="bg-blue-600 text-white p-4" data-id="element-133">
              <h3 className="text-xl font-bold flex items-center gap-2" data-id="element-134">
                <span className="text-2xl" data-id="element-135">🏋️</span> TOMO 4 – Nivel Avanzado
              </h3>
            </div>
            <div className="p-6" data-id="element-136">
              <p className="text-gray-500 font-semibold mb-4 uppercase text-sm tracking-wide" data-id="element-137">
                Entrenamiento planificado como un profesional:
              </p>
              <ul className="space-y-3" data-id="element-138">
                <CheckListItem text="Cuerpo completo avanzado" data-id="element-139" />
                <CheckListItem text="División AB, ABC y ABCD" data-id="element-140" />
                <CheckListItem text="Mayor intensidad y enfoque muscular" data-id="element-141" />
                <CheckListItem text="Optimización de hipertrofia y rendimiento" data-id="element-142" />
              </ul>
              <div className="mt-6 p-3 bg-blue-50 text-blue-800 font-bold text-center rounded-lg" data-id="element-143">
                👉 Para exprimir tu máximo potencial.
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* BEFORE VS AFTER */}
      <Section className="bg-white" data-id="element-144">
        <h2 className="text-4xl font-black text-center mb-12" data-id="element-145">
          🔄 Antes vs Después
        </h2>

        <div className="grid md:grid-cols-2 gap-0 border-4 border-gray-200 rounded-2xl overflow-hidden shadow-xl" data-id="element-146">
          <div className="bg-red-50 p-8 md:p-12 border-b md:border-b-0 md:border-r border-gray-200" data-id="element-147">
            <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center gap-2" data-id="element-148">
              <X className="w-8 h-8" data-id="element-149" /> Antes del PACK
            </h3>
            <ul className="space-y-4" data-id="element-150">
              <li className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-151">
                <X className="w-5 h-5 text-red-500" data-id="element-152" /> Rutinas al azar
              </li>
              <li className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-153">
                <X className="w-5 h-5 text-red-500" data-id="element-154" /> Dudas constantes
              </li>
              <li className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-155">
                <X className="w-5 h-5 text-red-500" data-id="element-156" /> Progreso lento
              </li>
              <li className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-157">
                <X className="w-5 h-5 text-red-500" data-id="element-158" /> Miedo a lesionarte
              </li>
              <li className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-159">
                <X className="w-5 h-5 text-red-500" data-id="element-160" /> Confusión nutricional
              </li>
              <li className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-161">
                <X className="w-5 h-5 text-red-500" data-id="element-162" /> Falta de control
              </li>
            </ul>
          </div>

          <div className="bg-green-50 p-8 md:p-12" data-id="element-163">
            <h3 className="text-2xl font-bold text-green-700 mb-6 flex items-center gap-2" data-id="element-164">
              <Check className="w-8 h-8" data-id="element-165" /> Después del PACK
            </h3>
            <ul className="space-y-4" data-id="element-166">
              <li className="flex items-center gap-3 text-gray-800 font-bold" data-id="element-167">
                <Check className="w-5 h-5 text-green-600" data-id="element-168" /> Entrenamiento con
                criterio
              </li>
              <li className="flex items-center gap-3 text-gray-800 font-bold" data-id="element-169">
                <Check className="w-5 h-5 text-green-600" data-id="element-170" /> Rutinas según tu
                nivel
              </li>
              <li className="flex items-center gap-3 text-gray-800 font-bold" data-id="element-171">
                <Check className="w-5 h-5 text-green-600" data-id="element-172" /> Progresión real
              </li>
              <li className="flex items-center gap-3 text-gray-800 font-bold" data-id="element-173">
                <Check className="w-5 h-5 text-green-600" data-id="element-174" /> Más seguridad y
                técnica
              </li>
              <li className="flex items-center gap-3 text-gray-800 font-bold" data-id="element-175">
                <Check className="w-5 h-5 text-green-600" data-id="element-176" /> Nutrición entendida
                y aplicable
              </li>
              <li className="flex items-center gap-3 text-gray-800 font-bold" data-id="element-177">
                <Check className="w-5 h-5 text-green-600" data-id="element-178" /> Control total de tu
                proceso
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 text-center" data-id="element-179">
          <CTAButton text="🔥 QUIERO TRANSFORMAR MI ENTRENAMIENTO AHORA" data-id="element-180" />
        </div>
      </Section>

      {/* BONUSES */}
      <section className="bg-gradient-to-b from-white to-yellow-50 py-6 md:py-8 px-4 md:px-8" data-id="element-181">
        <div className="max-w-5xl mx-auto" data-id="element-182">
          <div className="text-center mb-8" data-id="element-183">
            <h2 className="text-4xl md:text-5xl font-black text-red-600 mb-2" data-id="element-184">
              🎁 BONOS ESPECIALES – GRATIS HOY
            </h2>
            <p className="text-xl font-bold text-gray-600 uppercase tracking-widest" data-id="element-185">
              (Incluidos solo por tiempo limitado)
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-186">
            {/* BONO 1 */}
            <div className="bg-white p-5 rounded-2xl shadow-xl border-2 border-yellow-400 relative" data-id="element-187">
              <div className="bg-red-600 text-white text-xs font-bold px-3 py-1 absolute top-4 right-4 rounded-full uppercase z-10" data-id="element-188">
                Bono #1
              </div>
              <div className="mb-4 cursor-pointer flex justify-center" onClick={scrollToPurchase} data-id="element-189">
                <img src={BONO_1_IMAGE} alt="Guía de Nutrición" className="w-full max-w-sm aspect-square object-cover rounded-lg" data-id="element-190" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-191">
                Guía de Nutrición para la Hipertrofia
              </h3>
              <div className="flex items-center gap-3 mb-3" data-id="element-192">
                <span className="text-gray-400 line-through text-lg" data-id="element-193">
                  Valor: ARS $18.000
                </span>
                <span className="text-green-600 font-black text-xl" data-id="element-194">
                  HOY: GRATIS
                </span>
              </div>
              <ul className="space-y-2 text-gray-700" data-id="element-195">
                <li className="flex items-start gap-2" data-id="element-196">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-197" /> Principios
                  de nutrición deportiva
                </li>
                <li className="flex items-start gap-2" data-id="element-198">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-199" />{' '}
                  Macronutrientes y micronutrientes
                </li>
                <li className="flex items-start gap-2" data-id="element-200">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-201" /> Cálculo de
                  calorías
                </li>
                <li className="flex items-start gap-2" data-id="element-202">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-203" /> Comidas pre
                  y post entrenamiento
                </li>
                <li className="flex items-start gap-2" data-id="element-204">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-205" /> Recetas
                  prácticas
                </li>
                <li className="flex items-start gap-2" data-id="element-206">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-207" /> Cómo
                  sostener la dieta en el tiempo
                </li>
              </ul>
              <p className="mt-3 text-sm font-bold text-gray-500 italic" data-id="element-208">
                👉 Sin dietas extremas. Sin confusión.
              </p>
            </div>

            {/* BONO 2 */}
            <div className="bg-white p-5 rounded-2xl shadow-xl border-2 border-yellow-400 relative" data-id="element-209">
              <div className="bg-red-600 text-white text-xs font-bold px-3 py-1 absolute top-4 right-4 rounded-full uppercase z-10" data-id="element-210">
                Bono #2
              </div>
              <div className="mb-4 cursor-pointer flex justify-center" onClick={scrollToPurchase} data-id="element-211">
                <img src={BONO_2_IMAGE} alt="20 Errores Dieta" className="w-full max-w-sm aspect-square object-cover rounded-lg" data-id="element-212" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-213">
                20 Errores en tu Dieta
              </h3>
              <div className="flex items-center gap-3 mb-3" data-id="element-214">
                <span className="text-gray-400 line-through text-lg" data-id="element-215">
                  Valor: Invaluable
                </span>
                <span className="text-green-600 font-black text-xl" data-id="element-216">
                  HOY: GRATIS
                </span>
              </div>
              <ul className="space-y-2 text-gray-700" data-id="element-217">
                <li className="flex items-start gap-2" data-id="element-218">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-219" /> Errores que
                  frenan tu progreso
                </li>
                <li className="flex items-start gap-2" data-id="element-220">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-221" /> Alcohol,
                  fines de semana y suplementos
                </li>
                <li className="flex items-start gap-2" data-id="element-222">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-223" /> Mala
                  planificación
                </li>
                <li className="flex items-start gap-2" data-id="element-224">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-225" /> Información
                  incorrecta
                </li>
                <li className="flex items-start gap-2" data-id="element-226">
                  <Check className="w-4 h-4 text-green-500 mt-1" data-id="element-227" /> Comida
                  trampa vs refeed
                </li>
              </ul>
              <p className="mt-3 text-sm font-bold text-gray-500 italic" data-id="element-228">
                👉 Corregir errores = avanzar más rápido.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGE SUMMARY + PRICING */}
      <Section id="compra" className="bg-black text-white border-y-8 border-yellow-500" data-id="element-229">
        <div className="max-w-4xl mx-auto text-center" data-id="element-230">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-10 underline decoration-yellow-500 decoration-4 underline-offset-8" data-id="element-231">
            📦 Lo que recibís hoy
          </h2>

          <div className="bg-gray-900 p-8 rounded-2xl border border-gray-700 mb-10 text-left max-w-2xl mx-auto shadow-2xl" data-id="element-232">
            <ul className="space-y-4 text-lg md:text-xl" data-id="element-233">
              <li className="flex items-center gap-3" data-id="element-234">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-235" />{' '}
                <span data-id="element-236">
                  📘 Tomos completos de entrenamiento (principiante a avanzado)
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-237">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-238" />{' '}
                <span data-id="element-239">
                  🥗{' '}
                  <span className="font-bold text-yellow-400" data-id="element-240">
                    BONO GRATIS:
                  </span>{' '}
                  Guía de nutrición para hipertrofia
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-241">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-242" />{' '}
                <span data-id="element-243">
                  🚫{' '}
                  <span className="font-bold text-yellow-400" data-id="element-244">
                    BONO GRATIS:
                  </span>{' '}
                  Guía de errores alimenticios
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-245">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-246" />{' '}
                <span data-id="element-247">📂 Sistema claro y replicable</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-248">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-249" />{' '}
                <span data-id="element-250">⚡ Acceso inmediato</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-251">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-252" />{' '}
                <span data-id="element-253">💻 Descarga digital</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-254">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-255" />{' '}
                <span data-id="element-256">♾️ Acceso de por vida</span>
              </li>
            </ul>
          </div>

          <div className="mb-10" data-id="element-257">
            <p className="text-gray-400 text-xl md:text-2xl line-through mb-2" data-id="element-258">
              Precio regular: ARS $49.990
            </p>
            <p className="text-yellow-400 text-5xl md:text-7xl font-black animate-pulse" data-id="element-259">
              🔥 HOY SOLO: ARS $19,990
            </p>
          </div>

          <div className="flex flex-col items-center" data-id="element-260">
            <CTAButton text="👉 OBTENER MI FITNESS MASTER PACK AHORA" className="w-full md:w-auto text-lg md:text-3xl py-6 px-10" isExternal={true} data-id="element-261" />
            <div className="mt-6 flex items-center justify-center gap-4 opacity-70" data-id="element-262">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/MasterCard_Logo.svg/1200px-MasterCard_Logo.svg.png" alt="Mastercard" className="h-8 bg-white rounded p-1" data-id="element-263" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/1200px-Visa_Inc._logo.svg.png" alt="Visa" className="h-8 bg-white rounded p-1" data-id="element-264" />
              <span className="text-sm" data-id="element-265">Pago 100% Seguro</span>
            </div>
          </div>
        </div>
      </Section>

      {/* GUARANTEE */}
      <Section className="bg-white" data-id="element-266">
        <div className="max-w-4xl mx-auto border-4 border-dashed border-gray-300 p-8 md:p-12 rounded-3xl flex flex-col md:flex-row items-center gap-8" data-id="element-267">
          <div className="flex-shrink-0" data-id="element-268">
            <Shield className="w-32 h-32 text-black" data-id="element-269" />
          </div>
          <div data-id="element-270">
            <h2 className="text-3xl font-black mb-4" data-id="element-271">🔒 Garantía sin riesgo</h2>
            <p className="text-xl text-gray-700 leading-relaxed" data-id="element-272">
              Probalo durante <span className="font-bold" data-id="element-273">7 días</span>. Si no
              te da claridad, estructura y control sobre tu entrenamiento y tu
              alimentación, te devolvemos el 100% del dinero.
              <br data-id="element-274" />
              <br data-id="element-275" />
              <span className="font-bold" data-id="element-276">Sin preguntas.</span>
            </p>
          </div>
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section className="bg-gray-50" data-id="element-277">
        <h2 className="text-4xl font-black text-center mb-12" data-id="element-278">
          💬 Lo que dicen personas como vos
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-279">
          {[{
          quote: 'Entrenaba hace años sin entender por qué no progresaba. Este pack me ordenó todo.',
          name: 'Lucas',
          age: '32 años',
          city: 'Argentina'
        }, {
          quote: 'Por fin entendí cómo entrenar según mi nivel y cómo comer sin obsesionarme.',
          name: 'Mariana',
          age: '28 años',
          city: 'Córdoba'
        }, {
          quote: 'Como estudiante de educación física, es material que realmente suma.',
          name: 'Facundo',
          age: '24 años',
          city: 'Rosario'
        }, {
          quote: 'Dejé de copiar rutinas y empecé a entrenar con criterio.',
          name: 'Santiago',
          age: '35 años',
          city: 'Mendoza'
        }, {
          quote: 'Simple, claro y aplicable. No es humo.',
          name: 'Valeria',
          age: '41 años',
          city: 'Buenos Aires'
        }].map((t, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-md border border-gray-100" data-id="element-280">
              <div className="flex text-yellow-400 mb-4" data-id="element-281">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-5 h-5 fill-current" data-id="element-282" />)}
              </div>
              <p className="text-gray-700 italic mb-6 text-lg" data-id="element-283">"{t.quote}"</p>
              <div className="flex items-center gap-3" data-id="element-284">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center font-bold text-gray-500" data-id="element-285">
                  {t.name[0]}
                </div>
                <div data-id="element-286">
                  <p className="font-bold text-gray-900" data-id="element-287">{t.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-288">
                    {t.age} – {t.city}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-white" data-id="element-289">
        <h2 className="text-4xl font-black text-center mb-12" data-id="element-290">
          ❓ Preguntas frecuentes
        </h2>

        <div className="max-w-3xl mx-auto space-y-4" data-id="element-291">
          {[{
          q: '¿Es material digital?',
          a: 'Sí. Acceso inmediato desde cualquier dispositivo.'
        }, {
          q: '¿Sirve si recién empiezo?',
          a: 'Sí. Empieza desde cero y progresa por niveles.'
        }, {
          q: '¿Sirve si ya entreno hace tiempo?',
          a: 'Sí. Te ordena, te da estructura y corrige errores.'
        }, {
          q: '¿Es solo rutinas?',
          a: 'No. Es un sistema educativo + práctico.'
        }].map((item, i) => <div key={i} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-292">
              <button onClick={() => toggleFaq(i)} className="w-full flex items-center justify-between p-5 bg-gray-50 hover:bg-gray-100 transition-colors text-left" data-id="element-293">
                <span className="font-bold text-lg text-gray-900" data-id="element-294">
                  {item.q}
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-295" />
              </button>
              {openFaq === i && <div className="p-5 bg-white border-t border-gray-200 text-gray-700 font-medium" data-id="element-296">
                  {item.a}
                </div>}
            </div>)}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="bg-black text-white text-center pb-24" data-id="element-297">
        <h2 className="text-3xl md:text-5xl font-black mb-6" data-id="element-298">
          Entrenar puede ser más simple
        </h2>

        <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto" data-id="element-299">
          No necesitás más motivación. No necesitás copiar más rutinas.
          <br data-id="element-300" />
          <span className="text-white font-bold text-2xl mt-4 block" data-id="element-301">
            👉 Necesitás un sistema que te guíe.
          </span>
        </p>

        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-12 font-bold text-yellow-400 text-lg md:text-xl" data-id="element-302">
          <span className="flex items-center gap-2" data-id="element-303">
            <Zap className="w-5 h-5" data-id="element-304" /> Entrená con técnica
          </span>
          <span className="flex items-center gap-2" data-id="element-305">
            <Zap className="w-5 h-5" data-id="element-306" /> Comé con estrategia
          </span>
          <span className="flex items-center gap-2" data-id="element-307">
            <Zap className="w-5 h-5" data-id="element-308" /> Progresá sin lesiones
          </span>
        </div>

        <CTAButton text="👉 ACCEDÉ AHORA AL FITNESS MASTER PACK" className="w-full md:w-auto text-xl md:text-3xl" data-id="element-309" />
      </Section>

      <footer className="bg-gray-900 text-gray-500 py-8 text-center text-sm" data-id="element-310">
        <p data-id="element-311">
          © {new Date().getFullYear()} Fitness Master Pack. Todos los derechos
          reservados.
        </p>
        <p className="mt-2" data-id="element-312">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}